import { Component, inject, computed } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	ReLines,
	SIGNING_LOCATION_TYPE,
	SIGNING_REGION_TYPE,
} from '@models/businesses';
import { MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessAddressesState } from '@state/business/address/address.state';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { ButtonGroupOption } from '../../../../shared/atomic/atoms/button-group/button-group.component';
import {
	EditingDefaultsDialogComponent,
	ReLineType,
} from '../editing-dialog/editing-defaults-dialog.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { BarristerSolicitorFormatDrawerComponent } from '../barrister-solicitor-format-drawer/barrister-solicitor-format-drawer.component';
import { DateLineFormatComponent } from '../date-line-format/date-line-format.component';
import { formatDateLine } from '../../../../shared/core/util/date-line-format';

@UntilDestroy()
@Component({
	selector: 'app-general-defaults',
	templateUrl: './general.component.html',
	standalone: false,
})
export class GeneralDefaultsComponent {
	public currentMatterType: MatterFileType;
	public matterTypes: ButtonGroupOption<MatterFileType>[];
	public reLines: ReLines;
	public downloadedDocumentTitle: string;
	public remoteSigningJurat: string;
	public inPersonSigningJurat: string;
	public yoursTrulyLawyer: string;
	public emailSubjectSale: string;
	public emailSubjectPurchase: string;
	public emailSubjectRefinance: string;

	private store = inject(Store);

	reLines$: Observable<ReLines> = this.store.select(BusinessState.getReLines);
	downloadedDocumentTitle$: Observable<string> = this.store.select(
		BusinessState.getDownloadedDocumentTitle,
	);
	remoteSigningJurat$: Observable<string> = this.store.select(
		BusinessState.getRemoteSigningJurat,
	);
	inPersonSigningJurat$: Observable<string> = this.store.select(
		BusinessState.getInPersonSigningJurat,
	);
	yoursTrulyLawyer$: Observable<string> = this.store.select(
		BusinessState.getYoursTrulyLawyer,
	);
	emailSubjectSale$: Observable<string> = this.store.select(
		BusinessState.getDefaultEmailSubjectSale,
	);
	emailSubjectPurchase$: Observable<string> = this.store.select(
		BusinessState.getDefaultEmailSubjectPurchase,
	);
	emailSubjectRefinance$: Observable<string> = this.store.select(
		BusinessState.getDefaultEmailSubjectRefinance,
	);

	isFullyOnWord$: Observable<boolean> = this.store.select(
		BusinessState.isFullyOnWord,
	);

	dateLineFormat = this.store.selectSignal(
		BusinessState.getDefaultDateLineFormatSettings,
	);

	offices = this.store.selectSignal(BusinessAddressesState.getAll);
	businessUseSingularBarristerSolicitor = this.store.selectSignal(
		BusinessState.getUseSingularBarristerSolicitor,
	);

	dateLinePreviewText = computed(() => {
		if (!this.dateLineFormat()) return '';

		const locationInfo = {
			locationType: SIGNING_LOCATION_TYPE.CITY,
			city: 'Toronto',
			regionType: SIGNING_REGION_TYPE.PROVINCE,
			province: 'Ontario',
		};

		return (
			'Preview text settings: ' +
			formatDateLine(
				this.dateLineFormat(),
				locationInfo,
				'Apr 27, 2025',
				'27th day of April, 2025',
				this.dateLineFormat().signedRemotely,
			)
		);
	});

	hasOfficeSettings = computed(() => {
		const offices = this.offices();
		return offices.some(
			(office) => office.useSingularBarristerSolicitor !== undefined,
		);
	});

	constructor(
		private dialog: MatDialog,
		private drawerService: DrawerService,
	) {
		this.currentMatterType = MatterFileType.SALE;
		this.matterTypes = [
			{
				label: 'Sale',
				value: MatterFileType.SALE,
			},
			{
				label: 'Purchase',
				value: MatterFileType.PURCHASE,
			},
			{
				label: 'Refinance',
				value: MatterFileType.REFINANCE,
			},
		];
		this.reLines$
			.pipe(untilDestroyed(this))
			.subscribe((reLines) => (this.reLines = cloneDeep(reLines)));
		this.downloadedDocumentTitle$
			.pipe(untilDestroyed(this))
			.subscribe(
				(downloadedDocumentTitle) =>
					(this.downloadedDocumentTitle = cloneDeep(downloadedDocumentTitle)),
			);
		this.remoteSigningJurat$
			.pipe(untilDestroyed(this))
			.subscribe(
				(remoteSigningJurat) =>
					(this.remoteSigningJurat = cloneDeep(remoteSigningJurat)),
			);
		this.inPersonSigningJurat$
			.pipe(untilDestroyed(this))
			.subscribe(
				(inPersonSigningJurat) =>
					(this.inPersonSigningJurat = cloneDeep(inPersonSigningJurat)),
			);
		this.yoursTrulyLawyer$
			.pipe(untilDestroyed(this))
			.subscribe(
				(yoursTrulyLawyer) =>
					(this.yoursTrulyLawyer = cloneDeep(yoursTrulyLawyer)),
			);

		combineLatest([
			this.emailSubjectSale$,
			this.emailSubjectPurchase$,
			this.emailSubjectRefinance$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				([emailSubjectSale, emailSubjectPurchase, emailSubjectRefinance]) => {
					this.emailSubjectSale = cloneDeep(emailSubjectSale);
					this.emailSubjectPurchase = cloneDeep(emailSubjectPurchase);
					this.emailSubjectRefinance = cloneDeep(emailSubjectRefinance);
				},
			);
	}

	edit(type: ReLineType, tag: string, tagLabel: string): void {
		this.dialog.open(EditingDefaultsDialogComponent, {
			data: {
				matterType: this.currentMatterType,
				type,
				tag,
				tagLabel,
			},
			maxHeight: '90vh',
			maxWidth: '90vw',
			width: '40vw',
		});
	}

	editBarristerSolicitorFormat(): void {
		this.drawerService.openDrawer(BarristerSolicitorFormatDrawerComponent, {
			title: 'Barrister & Solicitor Settings',
			size: 'sm',
			noStyle: true,
			persist: true,
		});
	}

	editDateLineFormat(): void {
		this.drawerService.openDrawer(DateLineFormatComponent, {
			title: 'Dated Line',
			size: 'sm',
			noStyle: true,
			persist: true,
		});
	}
}
